import { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useInboxes } from "@/hooks/use-inboxes";
import { useInboxMessages } from "@/hooks/use-inbox-messages";
import { useAppStore } from "@/stores/app-store";
import { inboxesApi } from "@/api/inboxes";
import { InboxList } from "@/components/inbox/inbox-list";
import { MessageThread } from "@/components/inbox/message-thread";
import { InboxEmptyState } from "@/components/inbox/inbox-empty-state";
import { ContactDetailPanel } from "@/components/inbox/contact-detail-panel";

export function InboxPage() {
  const { inboxId } = useParams();
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined,
  );
  const [detailOpen, setDetailOpen] = useState(false);
  const [mobileView, setMobileView] = useState<"list" | "thread">("list");

  const selectedInboxId = useAppStore((s) => s.selectedInboxId);
  const setSelectedInbox = useAppStore((s) => s.setSelectedInbox);
  const channelFilter = useAppStore((s) => s.selectedChannelFilter);

  const activeInboxId = inboxId ? Number(inboxId) : selectedInboxId;
  const {
    inboxes,
    loading: inboxesLoading,
    refetch: refetchInboxes,
  } = useInboxes(statusFilter, channelFilter ?? undefined);
  const {
    messages,
    loading: messagesLoading,
    refetch: refetchMessages,
  } = useInboxMessages(activeInboxId);

  const activeInbox = inboxes.find((i) => i.id === activeInboxId);

  const handleSelectInbox = useCallback(
    (id: number) => {
      setSelectedInbox(id);
      setMobileView("thread");
    },
    [setSelectedInbox],
  );

  const handleBackToList = useCallback(() => {
    setMobileView("list");
  }, []);

  const handleToggleDetail = useCallback(() => {
    setDetailOpen((prev) => !prev);
  }, []);

  const handleStatusChange = useCallback(
    async (status: "open" | "pending" | "resolved") => {
      if (!activeInboxId) return;
      try {
        await inboxesApi.update(activeInboxId, { status });
        refetchInboxes();
      } catch {
        // Status change failed silently
      }
    },
    [activeInboxId, refetchInboxes],
  );

  const handleCloseConversation = useCallback(async () => {
    if (!activeInboxId) return;
    try {
      await inboxesApi.update(activeInboxId, { status: "resolved" });
      refetchInboxes();
    } catch {
      // Close failed silently
    }
  }, [activeInboxId, refetchInboxes]);

  return (
    <>
      {/* Desktop: flex-based panel layout */}
      <div className="hidden h-full md:flex">
        {/* Inbox list panel */}
        <div className="w-72 shrink-0 border-r">
          <InboxList
            inboxes={inboxes}
            loading={inboxesLoading}
            selectedId={activeInboxId}
            onSelect={handleSelectInbox}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
          />
        </div>

        {/* Message thread panel */}
        <div className="min-w-0 flex-1">
          {activeInboxId ? (
            <MessageThread
              inboxId={activeInboxId}
              inbox={activeInbox}
              messages={messages}
              loading={messagesLoading}
              onMessageSent={refetchMessages}
              onToggleDetail={handleToggleDetail}
              onStatusChange={handleStatusChange}
            />
          ) : (
            <InboxEmptyState />
          )}
        </div>

        {/* Contact detail panel */}
        {detailOpen && activeInbox && (
          <div className="w-72 shrink-0 border-l">
            <ContactDetailPanel
              inbox={activeInbox}
              onClose={handleCloseConversation}
            />
          </div>
        )}
      </div>

      {/* Mobile: single panel at a time */}
      <div className="flex h-full flex-col md:hidden">
        {mobileView === "list" ? (
          <InboxList
            inboxes={inboxes}
            loading={inboxesLoading}
            selectedId={activeInboxId}
            onSelect={handleSelectInbox}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
          />
        ) : activeInboxId ? (
          <div className="flex h-full flex-col">
            <button
              onClick={handleBackToList}
              className="flex items-center gap-2 border-b px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <span>&larr;</span>
              <span>Voltar para conversas</span>
            </button>
            <div className="flex-1 overflow-hidden">
              <MessageThread
                inboxId={activeInboxId}
                inbox={activeInbox}
                messages={messages}
                loading={messagesLoading}
                onMessageSent={refetchMessages}
                onToggleDetail={handleToggleDetail}
                onStatusChange={handleStatusChange}
              />
            </div>
          </div>
        ) : (
          <InboxEmptyState />
        )}
      </div>
    </>
  );
}
